import request from "@/utils/request";
import { EventBus } from "@/utils/bus";
import { USE_PERM_MENU, USE_PERM_BUTTON, PERM_EMPTY_AS_FULL } from "@/config";

/**
 * 权限状态与判定（RBAC）
 * 设计文档：docs/RBAC前端权限显示设计.md §3.2
 *
 * ⚠️ 四条「宁松勿紧」铁律（违反会导致全站白屏）：
 *   1. 权限接口失败 → loaded=false，hasPerm 一律返回 true
 *   2. hasPerm 未加载完成时 → 返回 true
 *   3. isPlatformUser 拿不到身份时 → 返回 true
 *   4. 权限码集合为空 → 视为尚未接入 RBAC，全部放行（PERM_EMPTY_AS_FULL）
 */

const STORE_KEY = "permInfo";

const state = {
  loaded: false,   // 是否成功拉到权限
  orgType: -1,     // 0平台 1运维 2业主 3厂家 4环保局
  platform: false, // 是否平台侧
  perms: [],       // 生效权限码（多角色并集）
  roles: [],       // 角色编码
};

export function permState() {
  return state;
}

function apply(data) {
  const d = data || {};
  state.loaded = true;
  state.orgType = d.orgType == null ? -1 : d.orgType;
  state.platform = !!d.platform;
  state.perms = Array.isArray(d.perms) ? d.perms : [];
  state.roles = Array.isArray(d.roles) ? d.roles : [];
}

/** 回到「未加载」状态 → 按铁律全部放行 */
function resetState() {
  state.loaded = false;
  state.perms = [];
  state.roles = [];
  state.platform = false;
  state.orgType = -1;
}

/** 刷新页面时先从本地恢复，避免首屏闪烁（随后仍会重新拉取） */
export function restorePermInfo() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) apply(JSON.parse(raw));
  } catch (e) {
    /* 忽略，退回未加载状态 */
  }
}

/**
 * 拉取权限画像。⚠️ 失败也 resolve，绝不 reject。
 * @returns {Promise<void>}
 */
export function loadPermInfo() {
  if (!USE_PERM_MENU && !USE_PERM_BUTTON) {
    return Promise.resolve();
  }

  // ⚠️ 未登录时绝不请求 /user/permInfo
  //    后端 /user/** 受登录拦截器保护 → 无 token 返回 code=501，
  //    响应拦截器再据此跳登录页 → 整页重载 → main.js 再次请求 → **无限刷新死循环**。
  //    登录页本身不设任何权限限制，未登录直接走「全部放行」分支即可。
  if (!localStorage.getItem("token")) {
    resetState();
    try {
      localStorage.removeItem(STORE_KEY);
    } catch (e) {
      /* 忽略 */
    }
    return Promise.resolve();
  }

  return request({ url: "/user/permInfo", method: "get" })
    .then((res) => {
      const body = (res && res.data) || {};
      if (String(body.code) !== "200") {
        throw new Error(body.message || "permInfo 返回异常");
      }
      apply(body.result);
      try {
        localStorage.setItem(STORE_KEY, JSON.stringify(state));
      } catch (e) {
        /* 忽略存储失败 */
      }
      refreshAllPerms();
    })
    .catch(() => {
      // 拿不到权限：置为未加载 → 后续判定全部放行，避免锁死界面
      resetState();
    });
}

/**
 * 权限到位后通知各页复查（解决「组件先渲染、权限后到达」的场景）
 */
export function refreshAllPerms() {
  EventBus.$emit("permLoaded");
}

/** 清除本地权限缓存（退出登录时调用） */
export function clearPermInfo() {
  resetState();
  try {
    localStorage.removeItem(STORE_KEY);
  } catch (e) {
    /* 忽略 */
  }
}

// ==================== 判定 ====================

/**
 * 是否已经接入 RBAC（拿到了至少一个权限码）
 * @returns {boolean} 未加载 → false（走放行分支）
 */
function configured() {
  return state.loaded && !(PERM_EMPTY_AS_FULL && state.perms.length === 0);
}

/**
 * 是否拥有某个权限码
 * @param {string} code
 * @returns {boolean} 未加载 / 开关关闭 / 空码 → true（放行）
 */
export function hasPerm(code) {
  if (!USE_PERM_BUTTON) return true;
  if (!code) return true;
  if (!configured()) return true;
  return state.perms.indexOf(code) !== -1;
}

/** 任一满足 */
export function hasAnyPerm(codes) {
  if (!USE_PERM_BUTTON) return true;
  if (!codes || !codes.length) return true;
  if (!configured()) return true;
  for (let i = 0; i < codes.length; i++) {
    if (state.perms.indexOf(codes[i]) !== -1) return true;
  }
  return false;
}

/** 全部满足 */
export function hasAllPerm(codes) {
  if (!USE_PERM_BUTTON) return true;
  if (!codes || !codes.length) return true;
  if (!configured()) return true;
  for (let i = 0; i < codes.length; i++) {
    if (state.perms.indexOf(codes[i]) === -1) return false;
  }
  return true;
}

/** 是否平台侧用户（未加载时放行） */
export function isPlatformUser() {
  if (!USE_PERM_BUTTON) return true;
  if (!state.loaded) return true;
  return state.platform;
}

/** 组织类型：0平台 1运维 2业主 3厂家 4环保局 */
export function orgType() {
  return state.orgType;
}

export default {
  permState,
  restorePermInfo,
  loadPermInfo,
  refreshAllPerms,
  clearPermInfo,
  hasPerm,
  hasAnyPerm,
  hasAllPerm,
  isPlatformUser,
  orgType,
};
